import type { PublicationCode } from '@/scopes/common/domain/models'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { difference, isEmpty, keys, omit, partial } from 'ramda'
import * as React from 'react'
import { use } from 'react'
import { useUpdateEffect } from 'react-use'
import { type DateOrRange, departureDateStore } from '@/infrastructure/departure-date/store'
import { useDepartureDateStore } from '@/infrastructure/departure-date/useDepartureDateStore'
import { useIntl } from '@/intl'
import { CatalogueDetailsContext } from '@/scopes/common/components/CatalogueDetailsContext'
import { getCatalogueTypeByPublicationCode } from '@/scopes/common/domain/functions/catalogues'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useDependencies } from '@/scopes/common/infrastructure/dependencies'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { createSearchFilterClickEvent } from '@/scopes/search/search/domain/analytics'
import { pricePerPersonFilterRangeValues } from '@/scopes/search/search/domain/policies/currencies'
import { getRelatedFilterTypesForCatalogueType } from '@/scopes/search/search/domain/policies/filters'
import { map } from '@/scopes/search/search/infrastructure/api/aiFiltersMapper'
import {
  SearchFilters,
  SearchFilterSelection,
  SearchFilterType,
  SearchMultiSelectFilterType,
  SearchResponse,
  SearchSelectFilterType,
  SearchValueFilterType,
  SelectedMultiOption,
  SelectedMultiOptionValues,
  SelectedOption,
  SelectedOptionValue,
  SelectedValue,
} from '@/scopes/search/search/infrastructure/api/types'
import { scrollToSearchElementDelayed } from '@/scopes/search/search/infrastructure/dom/scrollToSearchElement'
import { useSearchStore } from '@/scopes/search/search/infrastructure/store/useSearchStore'
import { useSearchDependencies } from '@/scopes/search/search/pages/SearchPage/context'
import { COMPULSORY_FILTER_TYPES } from './constants'
import { getMultiSelectFilterSelectedOption, getSelectFilterSelectedOption } from './getFilterOptions'

export function useAIFilterQuery() {
  const { searchStore, roomsParticipantsDistributionStore } = useDependencies()
  const { data } = useSearchQuery()
  const { userQuery, filterSelection, setIsAIFetching, setIsAIError, setUserQuery } = searchStore.getState()
  const { searchAPIClient } = useSearchDependencies()

  const fetchAIFilters = async () => {
    setIsAIFetching(true)
    setIsAIError(false)

    try {
      const result = await searchAPIClient.getAIFilters(userQuery)
      const aiFilterSelection = map(result, data?.filters)
      const newSelection = {
        ...filterSelection,
        ...aiFilterSelection,
      }

      if (aiFilterSelection.DepartureDate) {
        const { setDepartureDate, setOriginDate } = departureDateStore.getState()
        setOriginDate(
          Array.isArray(aiFilterSelection.DepartureDate)
            ? aiFilterSelection.DepartureDate[0]
            : aiFilterSelection.DepartureDate,
        )
        setDepartureDate(aiFilterSelection.DepartureDate as DateOrRange)
      }
      if (aiFilterSelection.Participants) {
        const { setRooms } = roomsParticipantsDistributionStore.getState()
        setRooms(aiFilterSelection.Participants)
      }

      searchStore.setState({ filterSelection: newSelection, aiFilterSelection })
    }
    catch (error) {
      console.warn(error)
      setUserQuery('')
      setIsAIError(true)
    }
    finally {
      setIsAIFetching(false)
    }
  }

  React.useEffect(() => {
    if (userQuery === '')
      return
    fetchAIFilters()
  }, [userQuery])
}

export function useSearchQuery() {
  const { searchStore } = useDependencies()
  const { searchAPIClient, dateFilterActions } = useSearchDependencies()
  const { pagination, sorting, filterSelection, isAIError, isAIFetching } = searchStore.getState()
  const isFlightOnly = filterSelection?.IsFlightOnly
  const queryParams = useSearchQueryParams()
  const { publicationCode, isPublicationCodeReady } = use(CatalogueDetailsContext)
  const catalogueType = getCatalogueTypeByPublicationCode(publicationCode as PublicationCode)
  const isAirlineFilterEnabled = useFeatureFlag('BAHAMAS_ENABLE_AIRLINE_FILTER')
  const requiredFilters = getRelatedFilterTypesForCatalogueType(
    catalogueType,
    Boolean(isFlightOnly),
    publicationCode as PublicationCode,
    { isAirlineFilterEnabled },
  )

  const searchCriteria = {
    pagination,
    sorting,
    filterSelection: isAirlineFilterEnabled ? filterSelection : omit(['Airline'], filterSelection),
    requiredFilters,
  }
  const filterInitialized = !isEmpty(filterSelection)

  const syncDateFilter = (_result: SearchResponse) => {
    const selectedLastMinute = filterSelection?.Lastminute as SelectedOptionValue<SearchFilters['Lastminute']>
    const selectedLastMinutesOption = _result.filters?.Lastminute?.options?.find(option => option.value === selectedLastMinute)
    if (selectedLastMinutesOption) {
      const range = dateFilterActions.getRangeFromLastMinuteSelection(selectedLastMinutesOption)
      if (range) {
        dateFilterActions.setDepartureDateFilterSelection(range, true)
      }
    }
  }

  const { isFetching, isLoading, ...rest } = useQuery({
    queryKey: ['search-results', queryParams],
    queryFn: async () => {
      const { initialAccommodationIds, clearInitialAccommodationIds } = searchStore.getState()
      const result = await searchAPIClient.getSearchResults({
        ...searchCriteria,
        accommodationIds: initialAccommodationIds,
      })
      if (initialAccommodationIds?.length)
        clearInitialAccommodationIds()
      syncDateFilter(result)
      return result
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retryOnMount: false,
    placeholderData: keepPreviousData,
    enabled: filterInitialized && isPublicationCodeReady,
  })

  return {
    ...rest,
    isFetching: !filterInitialized || !isPublicationCodeReady || isFetching || isAIFetching,
    isLoading: !filterInitialized || !isPublicationCodeReady || isLoading,
    isAIError,
  }
}

export function useSearchQuerySync() {
  const { filterSelectionQueryParamHandler } = useSearchDependencies()
  const { dataUpdatedAt } = useSearchQuery()
  const [lastUpdate, setLastUpdate] = React.useState(0)

  React.useEffect(() => {
    if (dataUpdatedAt > 0 && dataUpdatedAt !== lastUpdate) {
      if (lastUpdate !== 0) {
        scrollToSearchElementDelayed()
      }
      filterSelectionQueryParamHandler.update()
      setLastUpdate(dataUpdatedAt)
    }
  }, [dataUpdatedAt])
}

export function useSearchQueryParams() {
  const { pagination, sorting, filterSelection } = useSearchStore()
  const { searchPageQueryBuilder } = useDependencies()

  return React.useMemo(
    () => searchPageQueryBuilder.build({ pagination, sorting, filterSelection }),
    [filterSelection, pagination, sorting, searchPageQueryBuilder],
  )
}

export function useResults() {
  const { isLoading, isFetching, data, error } = useSearchQuery()

  return {
    isLoading,
    isFetching,
    error,
    totalResults: data?.totalResults ?? 0,
    results: data?.results ?? [],
    accommodationTips: data?.accommodationTips ?? [],
    filteredCollection: data?.filteredCollection ?? null,
    ga4FilterCollection: data?.ga4FilterCollection ?? null,
    ga4Impressions: data?.ga4Impressions ?? [],
  }
}

export function usePagination() {
  const { data } = useSearchQuery()
  const { pagination, setNextPage, setPreviousPage, setPage } = useSearchStore()
  const currentPage = pagination.offset / pagination.limit + 1
  const numberOfPages = data?.totalResults ? Math.ceil(data.totalResults / pagination.limit) : 0
  const isFirstPage = currentPage === 1
  return {
    isFirstPage,
    currentPage,
    numberOfPages,
    setNextPage: () => {
      setNextPage()
    },
    setPreviousPage: () => {
      setPreviousPage()
    },
    setPage: (value: number | string) => {
      setPage(value)
    },
  }
}

export function useSorting() {
  const { isLoading, isFetching } = useSearchQuery()
  const { sorting, setSortingBy } = useSearchStore()

  return {
    isLoading,
    isFetching,
    sorting,
    setSortingBy: (value: string) => {
      setSortingBy(value)
    },
  }
}

export function useValueFilter<K extends SearchValueFilterType>(filterType: K) {
  const { isLoading, isFetching } = useSearchQuery()
  const { setFilterSelection, filterSelection, resetFilterValues } = useSearchStore()

  return {
    isLoading,
    isFetching,
    selectedValue: filterSelection[filterType] as SelectedValue<SearchFilters[K]>,
    setSelection: (value: SearchFilterSelection[K]) => {
      partial(setFilterSelection, [filterType])(value)
    },
    resetFilterValues: partial(resetFilterValues, [filterType]),
  }
}

export function useSelectFilter<K extends SearchSelectFilterType>(filterType: K) {
  const { isLoading, isFetching, data } = useSearchQuery()
  const { setFilterSelection, filterSelection } = useSearchStore()

  const selectedOption = getSelectFilterSelectedOption(
    data?.filters ?? {},
    filterType,
    filterSelection[filterType] as SelectedOptionValue<SearchFilters[K]>,
  )

  return {
    isLoading,
    isFetching,
    selectedOption,
    selectedValue: (filterSelection[filterType] as SelectedOptionValue<SearchFilters[K]>) ?? null,
    options: (data?.filters[filterType]?.options as SelectedOption<SearchFilters[K]>) ?? [],
    setSelection: (value: SearchFilterSelection[K]) => {
      partial(setFilterSelection, [filterType])(value)
    },
  }
}

export function useMultiSelectFilter<K extends SearchMultiSelectFilterType>(filterType: K) {
  const { isLoading, isFetching, data } = useSearchQuery()
  const { setFilterSelection, filterSelection } = useSearchStore()

  const selectedOptions = getMultiSelectFilterSelectedOption(
    data?.filters ?? {},
    filterType,
    (filterSelection?.[filterType] as SelectedMultiOptionValues<SearchFilters[K]>) ?? [],
  )

  const filterSelectedValues = filterSelection[filterType]

  return {
    isLoading,
    isFetching,
    selectedOptions,
    selectedValues: (filterSelectedValues as SelectedMultiOptionValues<SearchFilters[K]>) ?? [],
    options: (data?.filters[filterType]?.options as SelectedMultiOption<SearchFilters[K]>) ?? [],
    setSelection: (value: SearchFilterSelection[K]) => {
      partial(setFilterSelection, [filterType])(value)
    },
  }
}

export function useAiFilter() {
  const { setUserQuery, userQuery } = useSearchStore()
  const { isFetching, isAIError } = useSearchQuery()
  return {
    userQuery,
    setUserQuery,
    isFetching,
    isAIError,
  }
}

export function useFilters() {
  const { isLoading, isFetching } = useSearchQuery()
  const { resetFilterValues, filterSelection } = useSearchStore()
  const { reset } = useDepartureDateStore()

  const resetOptionalFilters = React.useCallback(() => {
    const allFilterTypes = keys(filterSelection)
    const filterTypesToReset = difference<SearchFilterType>(allFilterTypes, COMPULSORY_FILTER_TYPES)
    if (filterTypesToReset.includes('DepartureDate')) {
      reset()
    }
    resetFilterValues(filterTypesToReset)
  }, [filterSelection, resetFilterValues])

  return {
    isLoading,
    isFetching,
    resetOptionalFilters,
  }
}

export function useControlledSliderWithOpenRange(
  selectedValue: [number, number | null] | undefined,
  setSelection: (selectedValue: [number, number | null]) => void,
) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const {
    minValue,
    maxValue,
    maxPossibleValue,
    step: rangeStep,
  } = pricePerPersonFilterRangeValues(intl.defaultCurrency)

  const ensuredSelectedValue = React.useMemo(
    () => {
      return selectedValue ?? ([minValue, maxPossibleValue] as [number, number | null])
    },
    [selectedValue, minValue, maxPossibleValue],
  )

  const [lowestSelectedValue, highestSelectedValue] = ensuredSelectedValue
  const selectedRange = [lowestSelectedValue, highestSelectedValue === null ? maxPossibleValue : highestSelectedValue]

  const [currentSelectedRange, setCurrentSelectedRange] = React.useState(selectedRange)

  useUpdateEffect(() => {
    setCurrentSelectedRange(selectedRange)
  }, [ensuredSelectedValue])

  const formattedRange = React.useMemo(() => {
    const [lowerValue, higherValue] = currentSelectedRange

    if (higherValue > maxValue)
      return `${intl.formatTruncatedCurrency(lowerValue)} - +${intl.formatTruncatedCurrency(maxValue)}`

    return `${intl.formatTruncatedCurrency(lowerValue)} - ${intl.formatTruncatedCurrency(higherValue)}`
  }, [currentSelectedRange, maxValue, intl])

  const handleValueCommit = ([lowerValue, higherValue]: number[]) => {
    setSelection([lowerValue, higherValue > maxValue ? null : higherValue])
    sendEvent(
      createSearchFilterClickEvent({
        element: 'slider',
        type: 'price',
        result: formattedRange.replace('.', '').replace(/\s/g, '').replace('-', ' '),
      }),
    )
  }

  const handleValueChange = (newSelectedRange: number[]) => {
    setCurrentSelectedRange(newSelectedRange)
  }

  return {
    labelProps: {
      formattedRange,
    },
    sliderProps: {
      step: rangeStep,
      min: minValue,
      max: maxPossibleValue,
      value: currentSelectedRange,
      onValueCommit: handleValueCommit,
      onValueChange: handleValueChange,
    },
  }
}
